import { createFileRoute } from '@tanstack/react-router';
import Hero from '../../components/Hero';
import { basicHead } from '../../utility/head';

export const Route = createFileRoute('/_marketing/data-processing-agreement')({
  component: RouteComponent,
  ssr: true,
  loader: ({ location }) => ({
    pathname: location.pathname,
    seo: {
      title: 'Data Processing Agreement - Stark Crawl',
      description:
        'Read the Stark Crawl Data Processing Agreement (DPA) for GDPR compliance — covering data isolation, sub-processors, and your rights.',
      keywords: 'Stark Crawl DPA, data processing agreement, GDPR compliance',
    },
  }),
  head: basicHead,
});

function RouteComponent() {
  return (
    <section className="py-[3.75rem] lg:py-20 px-5 border-b border-grey-t-800 grow">
      <Hero
        breadcrumb="Data Processing Agreement"
        title="Data Processing Agreement"
        pathname="/dpa"
      />
      <article className="mt-5 space-y-3 lg:mt-[3.75rem] max-w-[57.5rem] mx-auto prose prose-lg">
        <p className="text-sm text-gray-500">Last updated: May 2026</p>

        <h2 className="text-h2 font-semibold font-grotesque mt-10 mb-5">
          1. Introduction
        </h2>
        <p>
          This Data Processing Agreement (&quot;DPA&quot;) forms part of the
          Terms of Service between Aduro Creative Ltd, trading as Stark Crawl
          (&quot;Processor&quot;, &quot;we&quot;, &quot;us&quot;), and the
          customer agreeing to these terms (&quot;Controller&quot;,
          &quot;you&quot;).
        </p>
        <p>
          This DPA applies where and to the extent that we process Personal Data
          on your behalf in the course of providing our services. This DPA is
          designed to ensure compliance with the requirements of the UK General
          Data Protection Regulation (UK GDPR), the EU General Data Protection
          Regulation (EU GDPR), and other applicable data protection laws.
        </p>
        <p>
          By using our services, you agree to be bound by this DPA. If you are
          entering into this DPA on behalf of an organisation, you represent
          that you have the authority to bind that organisation.
        </p>

        <h2 className="text-h2 font-semibold font-grotesque mt-10 mb-5">
          2. Definitions
        </h2>
        <p>In this DPA, the following terms have the meanings set out below:</p>
        <ul className="list-disc ml-5 space-y-2 mt-5">
          <li>
            <strong>&quot;Data Protection Laws&quot;</strong> means the UK GDPR,
            EU GDPR, the Data Protection Act 2018, and any other applicable laws
            relating to the processing of Personal Data and privacy.
          </li>
          <li>
            <strong>&quot;Personal Data&quot;</strong> means any information
            relating to an identified or identifiable natural person that is
            processed by us on your behalf in connection with the services.
          </li>
          <li>
            <strong>&quot;Processing&quot;</strong> means any operation
            performed on Personal Data, including collection, recording,
            storage, retrieval, use, disclosure, and erasure.
          </li>
          <li>
            <strong>&quot;Data Subject&quot;</strong> means the identified or
            identifiable natural person to whom the Personal Data relates.
          </li>
          <li>
            <strong>&quot;Sub-processor&quot;</strong> means any third party
            engaged by us to process Personal Data on your behalf.
          </li>
          <li>
            <strong>&quot;Security Incident&quot;</strong> means any accidental
            or unlawful destruction, loss, alteration, unauthorised disclosure
            of, or access to, Personal Data.
          </li>
          <li>
            <strong>&quot;Standard Contractual Clauses&quot;</strong> or{' '}
            <strong>&quot;SCCs&quot;</strong> means the standard contractual
            clauses approved by the European Commission for international data
            transfers.
          </li>
        </ul>

        <h2 className="text-h2 font-semibold font-grotesque mt-10 mb-5">
          3. Scope and Purpose of Processing
        </h2>

        <h3 className="mt-5 mb-4 text-h3 font-semibold">3.1 Subject Matter</h3>
        <p>
          This DPA applies to the processing of Personal Data that you upload
          to, or that is collected through, the Stark Crawl platform in
          connection with your use of our services.
        </p>

        <h3 className="mt-5 mb-4 text-h3 font-semibold">
          3.2 Nature and Purpose
        </h3>
        <p>
          We process Personal Data for the purpose of providing you with
          AI-powered document search, retrieval, and chat functionality. This
          includes:
        </p>
        <ul className="list-disc ml-5 space-y-2 mt-5">
          <li>
            Retrieving content from sources you upload or authorise (including
            file uploads and OAuth-connected sources such as Google Drive,
            Dropbox, Notion, and public URLs)
          </li>
          <li>Converting retrieved files to markdown or plain text format</li>
          <li>
            Generating dense and sparse vector embeddings of your content and
            storing them, together with content chunks and metadata, in a
            managed vector database
          </li>
          <li>
            Sending user queries and the relevant retrieved chunks to a
            third-party large-language-model provider for inference, and
            returning the generated response to authorised users
          </li>
          <li>Displaying content to users you have authorised</li>
          <li>Storing access rules and sharing permissions</li>
          <li>
            Operating the service (transactional email, error monitoring,
            product analytics, billing)
          </li>
        </ul>

        <h3 className="mt-5 mb-4 text-h3 font-semibold">
          3.3 Types of Personal Data
        </h3>
        <p>
          The types of Personal Data processed depend on the content you upload
          and may include:
        </p>
        <ul className="list-disc ml-5 space-y-2 mt-5">
          <li>Names and contact information</li>
          <li>Employment information</li>
          <li>Financial information</li>
          <li>Any other Personal Data contained in your uploaded documents</li>
        </ul>

        <h3 className="mt-5 mb-4 text-h3 font-semibold">
          3.4 Categories of Data Subjects
        </h3>
        <p>
          Data Subjects may include your employees, customers, suppliers,
          business contacts, and any other individuals whose Personal Data is
          contained in the content you upload.
        </p>

        <h3 className="mt-5 mb-4 text-h3 font-semibold">3.5 Duration</h3>
        <p>
          We will process Personal Data for the duration of your use of our
          services and for 90 days following account termination, unless a
          longer retention period is required by law or requested by you.
        </p>

        <h2 className="text-h2 font-semibold font-grotesque mt-10 mb-5">
          4. Controller Obligations
        </h2>
        <p>As the Controller, you are responsible for:</p>
        <ul className="list-disc ml-5 space-y-2 mt-5">
          <li>
            Ensuring you have a lawful basis for processing the Personal Data
            you upload to our platform
          </li>
          <li>
            Providing any required notices to Data Subjects and obtaining any
            necessary consents
          </li>
          <li>Ensuring the accuracy and quality of Personal Data</li>
          <li>
            Complying with all applicable Data Protection Laws in your use of
            our services
          </li>
          <li>
            Determining the purposes and means of processing Personal Data
          </li>
          <li>
            Responding to Data Subject requests (with our assistance as set out
            in this DPA)
          </li>
          <li>
            Notifying us promptly of any changes that may affect our processing
            of Personal Data
          </li>
        </ul>

        <h2 className="text-h2 font-semibold font-grotesque mt-10 mb-5">
          5. Processor Obligations
        </h2>
        <p>As the Processor, we will:</p>
        <ul className="list-disc ml-5 space-y-2 mt-5">
          <li>
            Process Personal Data only on your documented instructions, unless
            required by law to do otherwise (in which case we will notify you
            before processing, unless prohibited by law)
          </li>
          <li>
            Ensure that persons authorised to process Personal Data have
            committed to confidentiality or are under an appropriate statutory
            obligation of confidentiality
          </li>
          <li>
            Implement appropriate technical and organisational measures to
            ensure a level of security appropriate to the risk
          </li>
          <li>
            Respect the conditions for engaging Sub-processors as set out in
            this DPA
          </li>
          <li>Assist you in responding to Data Subject requests</li>
          <li>
            Assist you in ensuring compliance with your obligations regarding
            security, breach notification, data protection impact assessments,
            and prior consultations
          </li>
          <li>
            Delete or return all Personal Data at the end of the provision of
            services, as set out in this DPA
          </li>
          <li>
            Make available to you information necessary to demonstrate
            compliance with our obligations and allow for audits
          </li>
          <li>
            Immediately inform you if, in our opinion, an instruction infringes
            Data Protection Laws
          </li>
        </ul>

        <h2 className="text-h2 font-semibold font-grotesque mt-10 mb-5">
          6. AI Processing
        </h2>
        <p>
          You acknowledge that our services involve AI processing of your
          content using hosted language-model inference in the United Kingdom
          and Ireland, and Pinecone for vector storage and retrieval.
          We confirm that:
        </p>
        <ul className="list-disc ml-5 space-y-2 mt-5">
          <li>
            <strong>Data isolation:</strong> Your Personal Data, content
            chunks, and embeddings are logically segregated by tenant and are
            not accessible to other customers or their knowledge bases.
          </li>
          <li>
            <strong>No model training:</strong> Our cloud provider does not
            use, and contractually requires the underlying foundation-model
            providers not to use, prompts or completions to train any models.
            Pinecone does not train models on
            customer data.
          </li>
          <li>
            <strong>Processing location:</strong> Application compute and
            storage and language-model inference are located in the United
            Kingdom and Ireland (AWS eu-west-2 and eu-west-1). Storage of
            embeddings in Pinecone takes place in the United States; the
            relevant transfer mechanisms are set out in section 8 and on our
            Sub-processors page.
          </li>
        </ul>

        <h2 className="text-h2 font-semibold font-grotesque mt-10 mb-5">
          7. Sub-processors
        </h2>

        <h3 className="mt-5 mb-4 text-h3 font-semibold">
          7.1 Authorised Sub-processors
        </h3>
        <p>
          You provide general authorisation for us to engage Sub-processors to
          process Personal Data on your behalf. The current list of authorised
          Sub-processors — including each Sub-processor&apos;s purpose, the
          categories of Personal Data processed, the processing location, and
          the applicable cross-border transfer mechanism — is maintained at{' '}
          <a href="/subprocessors">starkcrawl.com/subprocessors</a> and forms
          part of this DPA.
        </p>
        <p>
          For clarity, certain providers we use to operate our own business
          (for example product analytics, advertising measurement, and consent
          management on our marketing and product surfaces) are not
          Sub-processors under this DPA: in respect of those activities Stark
          Chat is the Controller of the limited operational and product-usage
          data collected, and customer-uploaded content is not routed through
          those tools. Those providers are disclosed in our Privacy Policy.
          The relationship with Stripe is split: Stripe acts as our
          Sub-processor for billing-contact metadata that we send to it
          (account email and company name), and as an independent Controller
          for full payment-card data, which Stark Crawl does not receive or
          store.
        </p>

        <h3 className="mt-5 mb-4 text-h3 font-semibold">
          7.2 Sub-processor Changes
        </h3>
        <p>
          We will notify you of any intended changes to our Sub-processors by
          updating the{' '}
          <a href="/subprocessors">Sub-processors page</a> and, where
          practicable, by email notification at least 14 days before the change
          takes effect. You may object to a new Sub-processor by notifying us
          in writing within 14 days of receiving notice. If you object on
          reasonable grounds relating to data protection, we will work with you
          to find a mutually acceptable resolution. If no resolution can be
          reached, you may terminate your account.
        </p>

        <h3 className="mt-5 mb-4 text-h3 font-semibold">
          7.3 Sub-processor Obligations
        </h3>
        <p>
          We will ensure that each Sub-processor is bound by data protection
          obligations no less protective than those set out in this DPA. We
          remain fully liable to you for the performance of our
          Sub-processors&apos; obligations.
        </p>

        <h2 className="text-h2 font-semibold font-grotesque mt-10 mb-5">
          8. International Data Transfers
        </h2>

        <h3 className="mt-5 mb-4 text-h3 font-semibold">
          8.1 Primary Processing Location
        </h3>
        <p>
          The application&apos;s compute, storage, and AI-inference
          infrastructure is located within the United Kingdom and the European
          Economic Area:
        </p>
        <ul className="list-disc ml-5 space-y-2 mt-5">
          <li>
            AWS eu-west-2 (London, UK) — application compute (Lambda),
            relational database (RDS), object storage (S3), key-value
            storage (DynamoDB), authentication (Cognito), email (SES), and
            message queues (SQS).
          </li>
          <li>
            AWS eu-west-1 (Ireland) and eu-west-2 (London) — language-model
            inference.
          </li>
        </ul>

        <h3 className="mt-5 mb-4 text-h3 font-semibold">
          8.2 Transfers Outside the UK/EEA
        </h3>
        <p>
          Certain Sub-processors that we rely on to deliver the service
          process Personal Data outside the United Kingdom and European
          Economic Area, principally in the United States. These currently
          include Pinecone (vector storage), Stripe (payments), Sentry (error
          monitoring), Cloudflare (DNS for customer custom domains), and
          Firecrawl (URL fetching). Where such transfers occur, we ensure
          that appropriate safeguards are in place, including:
        </p>
        <ul className="list-disc ml-5 space-y-2 mt-5">
          <li>
            Standard Contractual Clauses (SCCs) approved by the European
            Commission
          </li>
          <li>
            The UK International Data Transfer Agreement or UK Addendum to the
            EU SCCs, as applicable
          </li>
          <li>
            The EU–US Data Privacy Framework, where the recipient is currently
            certified under that framework
          </li>
          <li>
            Any other valid transfer mechanism under applicable Data Protection
            Laws
          </li>
        </ul>

        <h3 className="mt-5 mb-4 text-h3 font-semibold">
          8.3 Transfer Impact Assessments
        </h3>
        <p>
          We conduct transfer impact assessments for transfers to third
          countries to evaluate whether the recipient country provides an
          adequate level of data protection and whether supplementary measures
          are required.
        </p>

        <h2 className="text-h2 font-semibold font-grotesque mt-10 mb-5">
          9. Security Measures
        </h2>
        <p>
          We implement appropriate technical and organisational measures to
          protect Personal Data, including:
        </p>
        <ul className="list-disc ml-5 space-y-2 mt-5">
          <li>
            Encryption of Personal Data in transit using TLS 1.2 or higher
          </li>
          <li>Encryption of Personal Data at rest</li>
          <li>Access controls and authentication mechanisms</li>
          <li>Regular security assessments and testing</li>
          <li>Employee training on data protection and security</li>
          <li>Incident response procedures</li>
          <li>Business continuity and disaster recovery measures</li>
          <li>Logical separation of customer data</li>
        </ul>
        <p>
          We regularly review and update our security measures to address
          evolving threats and maintain an appropriate level of protection.
        </p>

        <h2 className="text-h2 font-semibold font-grotesque mt-10 mb-5">
          10. Security Incidents
        </h2>

        <h3 className="mt-5 mb-4 text-h3 font-semibold">10.1 Notification</h3>
        <p>
          We will notify you without undue delay, and in any event within 72
          hours, after becoming aware of a Security Incident affecting your
          Personal Data. Notification will be sent to the email address
          associated with your account.
        </p>

        <h3 className="mt-5 mb-4 text-h3 font-semibold">
          10.2 Notification Contents
        </h3>
        <p>Our notification will include, to the extent known:</p>
        <ul className="list-disc ml-5 space-y-2 mt-5">
          <li>A description of the nature of the Security Incident</li>
          <li>
            The categories and approximate number of Data Subjects affected
          </li>
          <li>
            The categories and approximate number of Personal Data records
            affected
          </li>
          <li>The likely consequences of the Security Incident</li>
          <li>
            The measures taken or proposed to address the Security Incident
          </li>
          <li>Contact details for obtaining further information</li>
        </ul>

        <h3 className="mt-5 mb-4 text-h3 font-semibold">10.3 Cooperation</h3>
        <p>
          We will cooperate with you and provide reasonable assistance in
          investigating and mitigating the Security Incident, and in meeting any
          notification obligations you may have to supervisory authorities or
          Data Subjects.
        </p>

        <h2 className="text-h2 font-semibold font-grotesque mt-10 mb-5">
          11. Data Subject Rights
        </h2>

        <h3 className="mt-5 mb-4 text-h3 font-semibold">
          11.1 Assistance with Requests
        </h3>
        <p>
          We will assist you in responding to requests from Data Subjects
          exercising their rights under Data Protection Laws, including rights
          of access, rectification, erasure, restriction, portability, and
          objection.
        </p>

        <h3 className="mt-5 mb-4 text-h3 font-semibold">
          11.2 Direct Requests
        </h3>
        <p>
          If we receive a request directly from a Data Subject relating to
          Personal Data you have uploaded, we will promptly notify you and will
          not respond to the request directly unless authorised by you or
          required by law.
        </p>

        <h3 className="mt-5 mb-4 text-h3 font-semibold">
          11.3 Response Timeframe
        </h3>
        <p>
          We will provide reasonable assistance to help you respond to Data
          Subject requests within the timeframes required by Data Protection
          Laws.
        </p>

        <h2 className="text-h2 font-semibold font-grotesque mt-10 mb-5">
          12. Data Protection Impact Assessments
        </h2>
        <p>
          Upon request, we will provide you with reasonable assistance in
          conducting data protection impact assessments and prior consultations
          with supervisory authorities, where required under Data Protection
          Laws, in relation to your use of our services.
        </p>

        <h2 className="text-h2 font-semibold font-grotesque mt-10 mb-5">
          13. Audits
        </h2>

        <h3 className="mt-5 mb-4 text-h3 font-semibold">13.1 Audit Rights</h3>
        <p>
          We will make available to you all information reasonably necessary to
          demonstrate our compliance with this DPA. You may conduct audits,
          including inspections, either directly or through an independent
          auditor, subject to the following conditions:
        </p>
        <ul className="list-disc ml-5 space-y-2 mt-5">
          <li>
            You provide us with at least 30 days&apos; prior written notice
          </li>
          <li>
            Audits are conducted during normal business hours and no more than
            once per year
          </li>
          <li>The auditor agrees to reasonable confidentiality obligations</li>
          <li>The audit does not unreasonably disrupt our operations</li>
          <li>You bear the costs of the audit</li>
        </ul>

        <h3 className="mt-5 mb-4 text-h3 font-semibold">
          13.2 Certifications and Reports
        </h3>
        <p>
          Where available, we may provide you with relevant certifications,
          audit reports, or other documentation to demonstrate our compliance
          with this DPA in lieu of an on-site audit.
        </p>

        <h2 className="text-h2 font-semibold font-grotesque mt-10 mb-5">
          14. Data Deletion and Return
        </h2>

        <h3 className="mt-5 mb-4 text-h3 font-semibold">
          14.1 Upon Termination
        </h3>
        <p>
          Upon termination of your account or upon your written request, we will
          delete all Personal Data processed on your behalf within 90 days,
          unless:
        </p>
        <ul className="list-disc ml-5 space-y-2 mt-5">
          <li>
            You request return of the Personal Data, in which case we will
            provide it in a commonly used format
          </li>
          <li>We are required by applicable law to retain the Personal Data</li>
          <li>
            Retention is necessary for the establishment, exercise, or defence
            of legal claims
          </li>
        </ul>

        <h3 className="mt-5 mb-4 text-h3 font-semibold">14.2 Certification</h3>
        <p>
          Upon your written request, we will provide written confirmation that
          Personal Data has been deleted in accordance with this DPA.
        </p>

        <h2 className="text-h2 font-semibold font-grotesque mt-10 mb-5">
          15. Liability
        </h2>
        <p>
          The liability of each party under this DPA is subject to the
          limitations and exclusions set out in our Terms of Service. Nothing in
          this DPA limits either party&apos;s liability for breaches of Data
          Protection Laws to the extent such liability cannot be limited under
          applicable law.
        </p>

        <h2 className="text-h2 font-semibold font-grotesque mt-10 mb-5">
          16. Governing Law
        </h2>
        <p>
          This DPA is governed by and construed in accordance with the laws of
          England and Wales. Any disputes arising from this DPA shall be subject
          to the exclusive jurisdiction of the courts of England and Wales.
        </p>

        <h2 className="text-h2 font-semibold font-grotesque mt-10 mb-5">
          17. Changes to This DPA
        </h2>
        <p>
          We may update this DPA from time to time to reflect changes in our
          processing activities, Sub-processors, or applicable laws. We will
          notify you of material changes by posting the updated DPA on our
          website and updating the &quot;Last updated&quot; date. For
          significant changes affecting your rights or obligations, we will
          provide advance notice by email.
        </p>

        <h2 className="text-h2 font-semibold font-grotesque mt-10 mb-5">
          18. Contact Us
        </h2>
        <p>If you have any questions about this DPA, please contact us:</p>
        <p>
          <strong>Email:</strong>{' '}
          <a href="mailto:privacy@starkcrawl.com">privacy@starkcrawl.com</a>
        </p>
        <p>
          <strong>Company:</strong> Aduro Creative Ltd (trading as Stark Crawl)
          <br />
          Company Number: 11200639
        </p>
        <p>
          <strong>Address:</strong> Aduro Creative Ltd (trading as Stark Crawl)
          <br />
          27 Old Gloucester Street, London, WC1N 3AX
        </p>
      </article>
    </section>
  );
}
